import styled, {css} from 'styled-components';

import type {ThemeAndAnyColor, WithThemeProp} from '../../../styles';
import type {BlackoutWeight} from '../../../styles/utils/getBlackoutColor';
import type {LighteningWeight} from '../../../styles/utils/getLighteningColor';
import type {StyledProps} from './types';

import {
  getBlackoutColor,
  getColor,
  getLighteningColor,
  gts,
  gtsi,
  ms,
} from '../../../styles';
import {platform} from '../../../utils/platform';
import {View} from '../../View';
import {ButtonIcon, getIconSize, hoverFill as iconHoverFill} from './Icon';
import {ButtonText, hoverColor as textHoverColor} from './Text';

const transparentColor = 'rgba(255, 255, 255, 0)';

const padding = css<StyledProps>`
  padding-inline: ${gts('padding.xxl')};

  ${p =>
    p.iconLeft &&
    css`
      padding-inline-start: ${Math.round(
        Number(gtsi('padding.xxl')(p)) - getIconSize(p.size) / 2.5,
      )}px;
    `}

  ${p =>
    p.iconRight &&
    css`
      padding-inline-end: ${Math.round(
        Number(gtsi('padding.xxl')(p)) - getIconSize(p.size) / 2.5,
      )}px;
    `}
`;

const getButtonHeight = (size: StyledProps['size']) => {
  switch (size) {
    case 'la':
      return 48;
    case 'sm':
      return 32;
    default:
      return 40;
  }
};

const height = css<StyledProps>`
  height: ${p => ms(getButtonHeight(p.size))};
`;

const singleIcon = css<StyledProps>`
  ${p =>
    p.isSingleIcon &&
    css`
      width: ${getButtonHeight(p.size)}px;
      padding-inline: 0;
    `};
`;

const border = css<StyledProps>`
  outline: none;
  border-style: solid;
  border-width: 1px;
`;

const getBorderColor =
  (scheme: StyledProps['scheme'], accentColor: StyledProps['accentColor']) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    if (accentColor) {
      return getColor(accentColor)(p);
    }

    switch (scheme) {
      case 'attention':
        return getColor('border.red')(p);
      case 'primary':
      case 'secondary':
        return getColor(`border.${scheme}`)(p);
      default:
        return transparentColor;
    }
  };

const activeBorderColor = css<StyledProps>`
  border-color: ${p => {
    switch (p.variant) {
      case 'secondary':
      case 'tertiary':
        return getBorderColor(p.scheme, p.accentColor)(p);
      default:
        return transparentColor;
    }
  }};
`;

const getBaseBorderColor = (p: StyledProps & WithThemeProp) => {
  switch (p.variant) {
    case 'outlined':
      return getBorderColor(p.scheme, p.accentColor)(p);
    case 'secondary':
      return getColor('border.middle')(p);
    default:
      return transparentColor;
  }
};

const borderColor = css<StyledProps>`
  border-color: ${getBaseBorderColor};

  ${platform.isWeb &&
  css<StyledProps>`
    &:hover {
      border-color: ${p => {
        if (p.disabled) {
          return undefined;
        }

        switch (p.variant) {
          case 'outlined':
            return transparentColor;
          case 'secondary':
          case 'tertiary':
            return getBorderColor(p.scheme, p.accentColor)(p);
          default:
            return getBaseBorderColor(p);
        }
      }};
    }

    &:active {
      ${activeBorderColor}
    }
  `}

  ${p =>
    p.isPressed &&
    css`
      ${activeBorderColor}
    `}

  ${p =>
    p.disabled &&
    css`
      border-color: ${() => {
        switch (p.variant) {
          case 'outlined':
          case 'secondary':
            return getColor('border.light');
          default:
            return getBaseBorderColor(p);
        }
      }};
    `}
`;

const getThemeBackgroundColor =
  (
    scheme: StyledProps['scheme'],
    options: {
      accentColor?: StyledProps['accentColor'];
      blackout?: BlackoutWeight;
      lightnening?: LighteningWeight;
    } = {},
  ) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    if (scheme) {
      let color: ThemeAndAnyColor = `button.${scheme}`;

      if (options.accentColor) {
        color = options.accentColor;
      }

      if (options.lightnening) {
        return getLighteningColor(color, options.lightnening)(p);
      }

      if (options.blackout) {
        return getBlackoutColor(color, options.blackout)(p);
      }

      return getColor(color)(p);
    }

    return transparentColor;
  };

const activeBackgroundColor = css<StyledProps>`
  background-color: ${p => {
    if (p.disabled) {
      return undefined;
    }

    switch (p.variant) {
      case 'outlined':
      case 'primary':
        return getThemeBackgroundColor(p.scheme, {
          accentColor: p.accentColor,
          blackout: 'light',
        });
      default:
        return getThemeBackgroundColor(p.scheme, {
          accentColor: p.accentColor,
          lightnening: 'strong',
        });
    }
  }};
`;

const getBaseBackgroundColor = (p: StyledProps & WithThemeProp) => {
  switch (p.variant) {
    case 'outlined':
    case 'tertiary':
      return transparentColor;
    case 'secondary':
      return getColor('button.white');
    default:
      return getThemeBackgroundColor(p.scheme, {accentColor: p.accentColor});
  }
};

const backgroundColor = css<StyledProps>`
  background-color: ${getBaseBackgroundColor};

  ${platform.isWeb &&
  css<StyledProps>`
    &:hover {
      background-color: ${p => {
        if (p.disabled) {
          return undefined;
        }

        switch (p.variant) {
          case 'outlined':
            return getThemeBackgroundColor(p.scheme, {
              accentColor: p.accentColor,
            });
          case 'primary':
            return getThemeBackgroundColor(p.scheme, {
              accentColor: p.accentColor,
              lightnening: 'light',
            });
          case 'tertiary':
            return getColor('button.white')(p);
          default:
            return undefined;
        }
      }};

      ${ButtonText} {
        ${textHoverColor}
      }

      ${ButtonIcon} {
        ${iconHoverFill}
      }
    }
  `}

  ${platform.isWeb &&
  css<StyledProps>`
    &:active {
      ${activeBackgroundColor}
    }
  `}

  ${p =>
    p.isPressed &&
    css`
      ${activeBackgroundColor}
    `}

  ${p =>
    p.disabled &&
    css`
      background-color: ${() => {
        switch (p.variant) {
          case 'primary':
            return getThemeBackgroundColor(p.scheme, {
              accentColor: p.accentColor,
              lightnening: 'medium',
            });
          default:
            return getBaseBackgroundColor(p);
        }
      }};
    `}
`;

const box = css<StyledProps>`
  box-sizing: border-box;
  position: relative;
  border-radius: ${gts('borderRadius.sm')};
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${padding}
  ${height}
  ${singleIcon}
`;

const cursor = css<StyledProps>`
  cursor: pointer;

  ${p =>
    p.disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const transition = css`
  transition: all ease 0.2s;
`;

export const ContentContainer = styled(View)<{isHidden: boolean}>`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${gts('padding.sm')};
  overflow: hidden;

  ${p =>
    p.isHidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`;

const base = css<StyledProps>`
  ${box}
  ${border}
  ${borderColor}
  ${backgroundColor}
  ${cursor}
  ${transition}

  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
`;

export const web = css<StyledProps>`
  ${base};
  display: inline-flex;
`;

export const native = css<StyledProps>`
  ${base}
`;
